import { buildShaderUniforms } from "@/components/RenderedDie/helpers/buildShaderUniforms";
import { getTextureAsSrgb } from "@/components/RenderedDie/helpers/getTextureAsSrgb";
import { setUserDataAnimationTargets } from "@/components/RenderedDie/helpers/setUserDataAnimationTargets";
import { useAnimationEventHandler } from "@/components/RenderedDie/hooks/useAnimationEventHandler";
import { useFaceUp } from "@/components/RenderedDie/hooks/useFaceUp";
import { useTargetedAnimations } from "@/components/RenderedDie/hooks/useTargetedAnimations";
import { useTextures } from "@/components/RenderedDie/hooks/useTextures";
import type { ISnippet } from "@/components/RenderedDie/ISnippet";
import alphaMapFrag from "@/components/RenderedDie/shaders/alpha_map.frag";
import defaultVert from "@/components/RenderedDie/shaders/default.vert";
import emissiveMapFrag from "@/components/RenderedDie/shaders/emissive_map.frag";
import {
  DieTypes,
  getMediaUrl,
  isAnimationsDisabled,
  isDebug,
} from "@/constants";
import { dieConfigurations } from "@/constants/diceConfig";
import { Context } from "@/context";
import { DieEventValues } from "@/enums";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import { useGLTF } from "@react-three/drei";
import { invalidate, useFrame, useThree } from "@react-three/fiber";
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import {
  Color,
  DoubleSide,
  FrontSide,
  Material,
  Mesh,
  NearestFilter,
  RepeatWrapping,
  ShaderMaterial,
  TextureLoader,
  Vector3,
  type WebGLProgramParametersWithUniforms,
} from "three";
import { SkeletonUtils } from "three-stdlib";
import { getBlackHoleMaterial } from "./helpers/getBlackHoleMaterial";
import { getMeshPhysicalMaterial } from "./helpers/getMeshPhysicalMaterial";
import { getRimeMaterial } from "./helpers/getRimeMaterial";
import { getTilingSpinnyMaterial } from "./helpers/getTilingSpinnyMaterial";
import { LiquidMaterial } from "./materials/LiquidMaterial";
import { LiquidMaterialDefinition } from "./materials/LiquidMaterialDefinition";
import {
  MIST_TEXTURE,
  SANGUINE_TEXTURE_BLACK,
  SANGUINE_TEXTURE_RED,
} from "./materials/materialTextures";
import { MistMaterial } from "./materials/MistMaterial";
import { MistMaterialDefinition } from "./materials/MistMaterialDefinition";
import { SanguineCoreLightMaterial } from "./materials/SanguineCoreLightMaterial";

type NodeProps = {
  name: string;
  id: string;
  preview?: boolean;
  onMaterialCreated?: (child: Mesh) => void;
  onReady: (name: string) => void;
};

export const Node: FC<NodeProps> = ({
  name,
  id,
  preview,
  onMaterialCreated,
  onReady,
}) => {
  const [isMeshReady, setIsMeshReady] = useState(false);
  const { gl } = useThree();
  const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
  const { lastStandardEvent, dieType, rollId, upsideValue } =
    useRenderedDiceStore((state) => state.renderedDice.entities[id]);
  const rollMessage = useRenderedDiceStore(
    (state) => state.rolls.entities[rollId]?.message,
  );
  const setId = rollMessage.data.setId;
  const manifest = useRenderedDiceStore(
    (state) => state.manifests.entities[setId],
  );
  const { nodeEnv: ne, env: e } = useContext(Context);
  const nodeEnv = ne || JSON.parse(self.name).nodeEnv;
  const env = e || JSON.parse(self.name).env;
  const mediaUrl = getMediaUrl(nodeEnv, env);
  const { values } = dieConfigurations[dieType];
  const setCodename = manifest.slug;
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);

  const gltfUrl = customUrlMap
    ? customUrlMap[manifest?.resources.models[manifest[name].modelFile]]
    : `${mediaUrl}/dice/bundles/${setCodename}/${manifest?.resources.models[manifest[name].modelFile]}`;
  const { parser, scene, animations } = useGLTF(gltfUrl);
  const gltfRef = useRef<Mesh>(undefined!);
  const loadedMaterialsRef = useRef<Record<string, Material>>({});
  const hasClonedRef = useRef(false);

  const clips = useTargetedAnimations(
    manifest.overrideSetId ?? setId,
    gltfRef,
    dieType,
    upsideValue,
    manifest.animationGroups,
    manifest[name].animations,
  );

  const combinedClips = useMemo(
    () =>
      !isAnimationsDisabled && clips ? [...clips, ...animations] : undefined,
    [animations, clips],
  );

  const isReadyForAnimations = useAnimationEventHandler(
    id,
    combinedClips,
    gltfRef,
    values,
    upsideValue,
    manifest[name].animations,
    preview,
  );

  useEffect(() => {
    if (isMeshReady && (isReadyForAnimations || isAnimationsDisabled)) {
      onReady(name);
    }
  }, [isMeshReady, isReadyForAnimations, onReady, name]);

  const clone = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    if (hasClonedRef.current) return;

    if (parser) {
      setUserDataAnimationTargets(scene, parser);
    }

    const clone = SkeletonUtils.clone(scene);
    clone.userData.name = name;
    // eslint-disable-next-line react-hooks/refs
    hasClonedRef.current = true;

    return clone;
  }, [name, parser, scene]);

  // Render materials
  const materials = manifest[name].materials;
  const textures = useTextures(setCodename, manifest.resources.textures);
  const customShadersRef = useRef<
    Record<
      string,
      {
        shader: WebGLProgramParametersWithUniforms;
        customDefinition?: ISnippet;
      }
    >
  >({});
  const globalTime = useRef(0);

  useEffect(() => {
    loadedMaterialsRef.current = {};
    const gltf = gltfRef.current;

    if (!gltf || gltf.visible) return;

    const meshes: Mesh[] = [];

    gltf.traverse((c) => {
      const child = c as Mesh;

      if (child.isMesh) {
        meshes.push(child);
      }
    });

    // Check for materials within this node that reference the same texture definition
    // so that we can clone them (to avoid issues with any modifications or animations).
    const textureUsageCounts: Record<string, number> = {};
    Object.values(materials).forEach((material) => {
      if (typeof material === "string") return;

      const properties = material?.properties;
      if (!properties) return;
      [
        // textures with known animations that require cloning
        properties.albedoTexture,
        properties.opacityTexture,
        properties.emissiveTexture,
      ]
        .filter((property) => property !== undefined)
        .forEach((textureDefinition) => {
          textureUsageCounts[textureDefinition] =
            (textureUsageCounts[textureDefinition] || 0) + 1;
        });
    });

    meshes.forEach((child, i) => {
      let material = child.material as Material;
      const materialName = material.name === "" ? "default" : material.name;

      // Check if we've already loaded this material
      if (loadedMaterialsRef.current[materialName]) {
        child.material = loadedMaterialsRef.current[materialName];
      } else {
        // For the case of node materials that are just a string
        // we grab the shared resources material definition
        const manifestMaterial =
          typeof materials[materialName] === "string"
            ? manifest.resources.materials[materials[materialName]]
            : materials[materialName];

        const props = manifestMaterial?.properties ?? {};
        const shader = manifestMaterial?.shader;
        const customMaterial = manifestMaterial?.nodeJson;
        const disableShadow = manifestMaterial?.disableShadow;

        // TODO: revisit this once particles need to use the custom shaders
        // so it'll be clearer how we can refactor and move this out of Node.tsx
        if (shader) {
          if (!manifest.shaders?.[shader]?.fragmentShader) {
            if (isDebug) {
              console.warn(
                `Shader ${shader} not found for material ${materialName} in node ${name}`,
              );
            }

            return;
          }

          const uniforms = buildShaderUniforms(
            manifestMaterial?.uniforms || {},
            textures,
            values,
            preview,
          );

          const shaderMaterial = new ShaderMaterial({
            uniforms,
            vertexShader: defaultVert,
            fragmentShader: manifest.shaders[shader].fragmentShader,
          });
          shaderMaterial.onBeforeCompile = (shader) => {
            customShadersRef.current[materialName] = { shader };
          };

          if (props?.alpha !== undefined) {
            shaderMaterial.opacity = props.alpha;
            shaderMaterial.transparent = true;
            shaderMaterial.depthWrite = false;
          }

          material = shaderMaterial;
        } else if (customMaterial) {
          if (customMaterial.includes("liquid")) {
            const liquidMaterial = new LiquidMaterial();
            if (customMaterial.includes("small")) {
              liquidMaterial.u_Cut = 0.3;
            }

            // dice of healing and heorism use the same shader which was built initially for healing
            // so we just need to adjust some initial uniforms
            if (manifest.slug === "heroism") {
              liquidMaterial.u_TopColor = new Color("rgb(0, 107, 255)");
              liquidMaterial.u_BottomColor = new Color("rgb(34, 130, 236)");
              liquidMaterial.u_Color = new Color("rgb(1, 6, 7)");
              liquidMaterial.u_Float1 = 1.4;
              liquidMaterial.u_Float2 = 1.0;
              liquidMaterial.u_Float3 = 1.0;
              liquidMaterial.u_Float4 = 5.0;
              liquidMaterial.u_Float5 = 0.6;
              liquidMaterial.u_Float6 = 5.0;
              liquidMaterial.u_Float7 = 0.58;
              liquidMaterial.u_Color1 = new Color("rgb(5, 150, 255)");
              liquidMaterial.u_Float8 = 0.59;
              liquidMaterial.u_Float9 = 8.1;
              liquidMaterial.u_Float10 = 0.42;
              liquidMaterial.u_Float11 = 0.69;
              liquidMaterial.u_Float12 = 4.35;
              liquidMaterial.u_Float13 = 0.05;
              liquidMaterial.u_Cut = 0.8;
              liquidMaterial.u_Float14 = 10.0;
              liquidMaterial.u_Float15 = 2.4;
              liquidMaterial.u_Intensity = 0.6;
              liquidMaterial.u_Float16 = -1.6;
              liquidMaterial.u_Color2 = new Color("rgb(20, 51, 253)");

              if (customMaterial.includes("medium")) {
                liquidMaterial.u_Cut = 0.45;
              }

              if (customMaterial.includes("small")) {
                liquidMaterial.u_Cut = 0.17;
              }
            }

            liquidMaterial.u_UpAxis = preview
              ? new Vector3(0, 1, 0)
              : new Vector3(0, 0, 1);
            liquidMaterial.side =
              props?.backFaceCulling === false ? DoubleSide : FrontSide;

            liquidMaterial.onBeforeCompile = (shader) => {
              customShadersRef.current[materialName] = {
                shader,
                customDefinition: new LiquidMaterialDefinition(
                  gltfRef.current,
                  shader,
                ),
              };
            };

            material = liquidMaterial;
          } else if (customMaterial.includes("sanguine")) {
            const sanguineMaterial = new SanguineCoreLightMaterial();
            sanguineMaterial.onBeforeCompile = (shader) => {
              const sanguineTextureRed = new TextureLoader().load(
                SANGUINE_TEXTURE_RED,
              );
              sanguineTextureRed.flipY = false;
              sanguineTextureRed.colorSpace = "srgb";
              sanguineTextureRed.minFilter = NearestFilter;
              sanguineTextureRed.anisotropy = maxAnisotropy;

              const sanguineTextureBlack = new TextureLoader().load(
                SANGUINE_TEXTURE_BLACK,
              );
              sanguineTextureBlack.flipY = false;
              sanguineTextureBlack.anisotropy = maxAnisotropy;

              shader.uniforms.TextureTexture.value = sanguineTextureRed;
              shader.uniforms.Texture1Texture.value = sanguineTextureBlack;

              customShadersRef.current[materialName] = { shader };
            };

            material = sanguineMaterial;
          } else if (customMaterial.includes("tilingSpinny")) {
            const tilingSpinnyMaterial = getTilingSpinnyMaterial(
              maxAnisotropy,
              (shader) => {
                customShadersRef.current[`${child.name}-${materialName}`] = {
                  shader,
                };
              },
            );

            material = tilingSpinnyMaterial;
          } else if (customMaterial.includes("black-hole")) {
            const blackHoleMaterial = getBlackHoleMaterial((shader) => {
              customShadersRef.current[`${child.name}-${materialName}`] = {
                shader,
              };
            });

            material = blackHoleMaterial;
          } else if (customMaterial.includes("mist")) {
            const mistMaterial = new MistMaterial({
              transparent: true,
              depthWrite: false,
            });
            mistMaterial.onBeforeCompile = (shader) => {
              const mistTexture = new TextureLoader().load(MIST_TEXTURE);
              mistTexture.flipY = false;
              mistTexture.wrapS = mistTexture.wrapT = RepeatWrapping;
              mistTexture.colorSpace = "srgb";
              mistTexture.anisotropy = maxAnisotropy;

              shader.uniforms.TextureTexture.value = mistTexture;

              customShadersRef.current[materialName] = {
                shader,
                customDefinition: new MistMaterialDefinition(
                  child,
                  shader,
                  preview,
                ),
              };
            };

            material = mistMaterial;
          }
        } else if (props?.emissiveTexture?.includes("RimeProceduralEmissive")) {
          const rimeMaterial = getRimeMaterial(
            props.numberTexture ?? "",
            props.albedoTexture ?? "",
            textures,
            (shader) => {
              customShadersRef.current[materialName] = { shader };
            },
          );

          material = rimeMaterial;
        } else {
          /**
           * All non-custom materials are treated as physical materials even if
           * the manifest does not provide other properties. This way the rest of
           * our logic and shader adjustments are applied consistently.
           * This is for sets like Mythic where the materials come with all properties
           * set in the GLB.
           **/

          let albedoTexture;
          let emissiveTexture;
          let opacityTexture;

          // handle texture updates beyond the defaults set in useTextures
          // and clone if used in multiple materials
          if (props.albedoTexture && textures[props.albedoTexture]) {
            if (
              textureUsageCounts[props.albedoTexture] > 1 ||
              props.albedoTextureClone
            ) {
              albedoTexture = getTextureAsSrgb(
                textures[props.albedoTexture],
              ).clone();
            } else {
              albedoTexture = getTextureAsSrgb(textures[props.albedoTexture]);
            }
          }
          if (props.emissiveTexture && textures[props.emissiveTexture]) {
            if (
              textureUsageCounts[props.emissiveTexture] > 1 ||
              props.emissiveTextureClone
            ) {
              emissiveTexture = getTextureAsSrgb(
                textures[props.emissiveTexture],
              ).clone();
            } else {
              emissiveTexture = getTextureAsSrgb(
                textures[props.emissiveTexture],
              );
            }
          }
          if (props.opacityTexture && textures[props.opacityTexture]) {
            if (
              textureUsageCounts[props.opacityTexture] > 1 ||
              props.opacityTextureClone
            ) {
              opacityTexture = textures[props.opacityTexture].clone();
            } else {
              opacityTexture = textures[props.opacityTexture];
            }
          }

          const physicalMaterial = getMeshPhysicalMaterial(
            name,
            material,
            props,
            textures,
            albedoTexture,
            emissiveTexture,
            opacityTexture,
          );

          // We're using a suggested workaround (userData also did not work)
          // for certain "deep" animations that don't work with the property binding
          // @see https://discourse.threejs.org/t/material-animations-using-animationclips/3540/5
          if (physicalMaterial.map) {
            // @ts-expect-error Animation system cannot reach `node.material.map.offset[x]`
            physicalMaterial.mapOffset = physicalMaterial.map.offset;
          }
          if (physicalMaterial.alphaMap) {
            // @ts-expect-error Animation system cannot reach `node.material.alphaMap.offset[x]`
            physicalMaterial.alphaMapOffset = physicalMaterial.alphaMap.offset;
          }
          if (physicalMaterial.emissiveMap) {
            // @ts-expect-error Animation system cannot reach `node.material.emissiveMap.offset[x]`
            physicalMaterial.emissiveMapOffset =
              physicalMaterial.emissiveMap.offset;
          }

          // we still need to include some shader overrides for non-die nodes
          // otherwise `onMaterialCreated` called below does its own shader modifications
          if (!onMaterialCreated) {
            physicalMaterial.onBeforeCompile = (shader) => {
              const emissiveMap = "#include <emissivemap_fragment>";
              shader.fragmentShader = shader.fragmentShader.replace(
                emissiveMap,
                `
                  ${emissiveMap}
                  ${emissiveMapFrag}
                `,
              );

              // Handles a difference between how babylon and three use alpha maps
              // Tested with Spelljammer numbers
              const alphaMap = "#include <alphamap_fragment>";
              shader.fragmentShader = shader.fragmentShader.replace(
                alphaMap,
                `
                  ${alphaMapFrag}
                `,
              );
            };
          }

          physicalMaterial.customProgramCacheKey = () => setId;

          material = physicalMaterial;
        }

        material.defines = {
          ...material.defines,
          USE_UV: "",
        };

        loadedMaterialsRef.current[materialName] = material;

        // Bigby's hand has an alpha of 0 yet is not marked `disableShadow`
        child.castShadow = disableShadow !== true && props?.alpha !== 0;
        child.material = material;

        onMaterialCreated?.(child);
      }

      if (meshes.length - 1 === i) {
        gltf.visible = true;
        setIsMeshReady(true);
      }
    });
  }, [
    materials,
    onMaterialCreated,
    textures,
    manifest,
    name,
    preview,
    values,
    maxAnisotropy,
    setId,
  ]);

  useEffect(() => {
    Object.values(customShadersRef.current).forEach(
      ({ shader, customDefinition }) => {
        if (shader.uniforms.lastEvent) {
          shader.uniforms.lastEvent.value = lastStandardEvent;
        }
        if (shader.uniforms.lastEventTime) {
          shader.uniforms.lastEventTime.value = globalTime.current;
        }

        if (
          shader.uniforms.dieValue &&
          lastStandardEvent === DieEventValues.rollComplete
        ) {
          const dieState =
            useRenderedDiceStore.getState().renderedDice.entities[id];

          let dieValue = dieState.upsideValue ?? 0;
          if (dieState.dieType === DieTypes.d100) {
            dieValue = dieValue === 0 ? 10 : dieValue / 10;
          }

          shader.uniforms.dieValue.value = dieValue;
        }

        if (
          shader.uniforms.u_Matrix &&
          lastStandardEvent === DieEventValues.rollComplete
        ) {
          customDefinition?.Update(0);
        }
      },
    );
  }, [id, lastStandardEvent]);

  useFrame((_, delta) => {
    if (lastStandardEvent === undefined) return;

    globalTime.current += delta;

    Object.values(customShadersRef.current).forEach(
      ({ shader, customDefinition }) => {
        shader.uniforms.iGlobalTime.value = globalTime.current;
        customDefinition?.Update(delta);
      },
    );

    invalidate();
  });

  useFaceUp(
    manifest[name].billboardMode === 2,
    gltfRef,
    preview,
    manifest[name].randomRotation,
  );

  return <primitive ref={gltfRef} object={clone!} visible={false} />;
};
